import { FieldSet, IDataTableColumn, useGridState } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { DogBaseSearch, DogOwnerApi, DogOwnerGridDto } from "../../api";
import { ERoutes } from "../../ERoutes";
import { CustomDataTable, ExportButton } from "../../components";
import { useGlobalData } from "../../contexts";
import { useApiService, useSearchApiWithLanguageRefresh, useTl } from "../../hooks";
import { ETLCodes } from "../../locales";

export interface IRequestDogOwnerListProps {
  dogId: number;
  showTemporary?: boolean;
  columns?: IDataTableColumn[];
  showExport?: boolean;
}

export const RequestDogOwnerList: React.FunctionComponent<IRequestDogOwnerListProps> = ({
  dogId,
  columns,
  showExport = true,
  showTemporary = true,
}) => {
  const { t } = useTl();
  const api = useApiService(DogOwnerApi);
  const { currentLanguage } = useGlobalData();
  const [sObjState, setsobjState] = React.useState<any>();

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5],
    pageSize: 5,
  });

  const searchFunction = React.useCallback(
    (sObj?) => {
      const sobj = DogBaseSearch.fromJS({
        ...sObj,
        dogId: dogId,
        languageId: currentLanguage.languageId,
        currentOwners: true,
        showTemporaryOwners: showTemporary,
      });
      setsobjState(sobj);
      return api.baseSearch(sobj);
    },
    [api, currentLanguage.languageId, dogId, showTemporary]
  );

  const { search, loading } = useSearchApiWithLanguageRefresh({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  React.useEffect(() => {
    if (dogId) search();
  }, [dogId, search]);

  const defaultColumns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "info",
        autoFitContent: true,
        render: (row: DogOwnerGridDto) => {
          if (row.isTemporaryOwner) {
            return <i className="fas fa-hourglass-half" />;
          }
        },
      },
      {
        header: () => t(ETLCodes.Name),
        fieldName: "name",
        render: (row: DogOwnerGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.personId}/detail/view`}>
            {row.computedName}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.OwnerFrom),
        fieldName: "ownerFrom",
      },
      {
        header: () => t(ETLCodes.OwnerTo),
        fieldName: "ownerTo",
      },
    ],
    [t]
  );

  return (
    <FieldSet
      title={t(ETLCodes.Owners)}
      rightElement={showExport && <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sObjState} />}
    >
      <CustomDataTable tableState={tableState} loading={loading} columns={columns ?? defaultColumns} />
    </FieldSet>
  );
};
